import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Send from 'lucide-react/dist/esm/icons/send';
import Paperclip from 'lucide-react/dist/esm/icons/paperclip';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import User from 'lucide-react/dist/esm/icons/user';
import Info from 'lucide-react/dist/esm/icons/info';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import Loader from 'lucide-react/dist/esm/icons/loader';
import StopCircle from 'lucide-react/dist/esm/icons/stop-circle';
import { streamExternalChat } from '../../services/api/discovery';
import { parseMarkdown, formatMessageTime } from '../../utils/markdown';
import type { ExternalPortalContext, DiscoveryMessage, AgentAction, SSEEvent } from './types';

const API_BASE = process.env.REACT_APP_API_URL || '';

function makeOptimisticMessage(
  overrides: Partial<DiscoveryMessage> & Pick<DiscoveryMessage, 'role' | 'content'>,
  portal: ExternalPortalContext | null,
): DiscoveryMessage {
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    request_id: portal?.request_id ?? '',
    stakeholder_id: overrides.role === 'user' ? (portal?.stakeholder.id ?? null) : null,
    metadata_json: null,
    channel: 'web',
    target_stakeholder_id: overrides.role === 'agent' ? (portal?.stakeholder.id ?? null) : null,
    sender_name: overrides.role === 'user' ? (portal?.stakeholder.name ?? null) : null,
    sender_email: overrides.role === 'user' ? (portal?.stakeholder.email ?? null) : null,
    created_at: new Date().toISOString(),
    ...overrides,
  };
}

const ExternalPortal: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [portal, setPortal] = useState<ExternalPortalContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<DiscoveryMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [interjectionAddressed, setInterjectionAddressed] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamBuf = useRef('');

  // ── Fetch portal context ──────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setError('Invalid link — no access token provided.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref');
        const url = `${API_BASE}/api/discovery/portal/${token}${ref ? `?ref=${ref}` : ''}`;
        const res = await fetch(url);
        if (!res.ok) {
          if (res.status === 404) throw new Error('This link is invalid or has expired.');
          if (res.status === 410) throw new Error('This discovery request has been closed.');
          throw new Error('Failed to load. Please try again later.');
        }
        const data: ExternalPortalContext = await res.json();
        if (cancelled) return;
        setPortal(data);
        setMessages(data.messages);
        setPaused(data.stakeholder.conversation_status === 'paused');
        setInterjectionAddressed(data.interjection_addressed);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Something went wrong.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  // ── Abort stream on unmount ───────────────────────────────────────
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  // ── Auto-scroll ───────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  // ── Send message ──────────────────────────────────────────────────
  const handleSend = useCallback(() => {
    const content = input.trim();
    if (!content || !token || streaming) return;

    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const userMsg = makeOptimisticMessage({ role: 'user', content }, portal);
    setMessages(prev => [...prev, userMsg]);
    setStreaming(true);
    setStreamingText('');
    streamBuf.current = '';

    abortRef.current = streamExternalChat(
      token,
      content,
      (text) => {
        streamBuf.current += text;
        setStreamingText(streamBuf.current);
      },
      (actions) => {
        if (actions?.some((a: any) => a.type === 'pause_conversation')) setPaused(true);
      },
      (messageId) => {
        const finalContent = streamBuf.current;
        const agentMsg = makeOptimisticMessage(
          { role: 'agent', content: finalContent, id: messageId || undefined },
          portal,
        );
        setMessages(prev => [...prev, agentMsg]);
        streamBuf.current = '';
        setStreamingText('');
        setStreaming(false);
      },
      () => {
        const errMsg = makeOptimisticMessage(
          { role: 'agent', content: 'Sorry, something went wrong. Please try again.' },
          portal,
        );
        setMessages(prev => [...prev, errMsg]);
        streamBuf.current = '';
        setStreamingText('');
        setStreaming(false);
      },
    );
  }, [input, token, streaming, portal]);

  // ── Stop streaming ────────────────────────────────────────────────
  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    const partial = streamBuf.current;
    if (partial) {
      const agentMsg = makeOptimisticMessage({ role: 'agent', content: partial }, portal);
      setMessages(prev => [...prev, agentMsg]);
    }
    streamBuf.current = '';
    setStreamingText('');
    setStreaming(false);
  }, [portal]);

  // ── File upload ───────────────────────────────────────────────────
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !token) return;

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);

      const res = await fetch(`${API_BASE}/api/discovery/portal/${token}/attachments`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) throw new Error('Upload failed');

      const userMsg = makeOptimisticMessage(
        { role: 'user', content: `[Attached file: ${file.name}]` },
        portal,
      );
      setMessages(prev => [...prev, userMsg]);
    } catch {
      const errMsg = makeOptimisticMessage(
        { role: 'agent', content: 'File upload failed. Please try again.' },
        portal,
      );
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [token, portal]);

  // ── Keyboard handling ─────────────────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // ── Auto-resize textarea ──────────────────────────────────────────
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.overflow = 'hidden';
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, 120);
    el.style.height = `${newHeight}px`;
    el.style.overflow = el.scrollHeight > 120 ? 'auto' : 'hidden';
  }, []);

  // ── Loading screen ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-page text-content flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader size={28} className="text-primary animate-spin" />
          <p className="text-content-tertiary text-sm">Loading portal&hellip;</p>
        </div>
      </div>
    );
  }

  // ── Error screen ──────────────────────────────────────────────────
  if (error || !portal) {
    return (
      <div className="min-h-screen bg-surface-page text-content flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-5 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-status-error/10 flex items-center justify-center">
            <AlertCircle size={32} className="text-status-error" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Unable to Load</h1>
          <p className="text-content-tertiary text-sm leading-relaxed">
            {error || 'This link is invalid or has expired. Please check with the person who shared it.'}
          </p>
        </div>
      </div>
    );
  }

  const isClosed = !portal.is_active;

  return (
    <div className="h-screen bg-surface-page text-content flex flex-col overflow-hidden">
      {/* ── Branding header ──────────────────────────────────────── */}
      <header className="bg-surface-page border-b border-subtle sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-2.5">
          <span className="text-lg font-bold tracking-[0.2em] text-content select-none">QUAZI</span>
          <span className="text-[11px] font-semibold text-brand/70 tracking-widest uppercase mt-0.5">
            Discovery
          </span>
        </div>
      </header>

      {/* ── Closed banner ────────────────────────────────────────── */}
      {isClosed && (
        <div className="bg-amber-500/10 border-b border-amber-500/20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2.5">
            <AlertCircle size={16} className="text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-300/90">
              This request has been closed. You can review the conversation but cannot send new messages.
            </p>
          </div>
        </div>
      )}

      {/* ── Chat area ────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-6">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 flex flex-col gap-5">
          {/* Request context card — scrolls with messages */}
          <div className="rounded-xl border border-subtle bg-surface-secondary p-4 space-y-3">
            {portal.request_title && (
              <h2 className="text-sm font-semibold text-content leading-snug">
                {portal.request_title}
              </h2>
            )}
            {portal.request_description && (
              <p className="text-xs text-content-tertiary leading-relaxed">
                {portal.request_description}
              </p>
            )}
            <div className="flex items-start gap-2 bg-brand/[0.06] border border-brand/10 rounded-lg px-3 py-2.5">
              <Info size={14} className="text-brand/60 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-content-secondary leading-relaxed">
                {portal.stakeholder.welcome_note || "You\u2019ve been invited to share your perspective on this feature request. The agent will ask you targeted questions."}
              </p>
            </div>
          </div>

          {messages.length === 0 && !streaming && (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
              <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mb-5">
                <Sparkles size={26} className="text-primary" />
              </div>
              <p className="text-content-muted text-sm max-w-xs leading-relaxed">
                Share your thoughts, requirements, or questions about this feature request.
                The discovery agent will guide the conversation.
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {msg.role === 'user' ? (
                  <div className="w-8 h-8 rounded-full bg-surface-secondary border border-default flex items-center justify-center">
                    <User size={15} className="text-content-tertiary" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center">
                    <Sparkles size={15} className="text-primary" />
                  </div>
                )}
              </div>

              <div className={`flex flex-col max-w-[80%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                {msg.sender_name && msg.role === 'user' && (
                  <span className="text-[11px] text-content-muted mb-1 px-1">{msg.sender_name}</span>
                )}
                <div
                  className={`px-4 py-2.5 text-sm leading-relaxed break-words [&_ul]:my-1.5 [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:pl-5 [&_li]:my-0.5 [&_code]:text-[0.8rem] [&_strong]:font-semibold ${
                    msg.role === 'user'
                      ? 'bg-brand/20 text-content rounded-2xl rounded-tr-sm'
                      : 'bg-surface-secondary text-content border border-subtle rounded-2xl rounded-tl-sm'
                  }`}
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }}
                />
                <span className="text-[10px] text-content-faint mt-1 px-1">
                  {formatMessageTime(msg.created_at)}
                </span>
              </div>
            </div>
          ))}

          {/* Streaming bubble */}
          {streaming && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-8 h-8 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center">
                  <Sparkles size={15} className="text-primary animate-pulse" />
                </div>
              </div>
              <div className="flex flex-col items-start max-w-[80%]">
                {streamingText ? (
                  <div className="px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm leading-relaxed bg-surface-secondary text-content border border-subtle break-words [&_ul]:my-1.5 [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:pl-5 [&_li]:my-0.5 [&_code]:text-[0.8rem]">
                    <span dangerouslySetInnerHTML={{ __html: parseMarkdown(streamingText) }} />
                    <span className="inline-block w-[3px] h-[1em] bg-brand/70 ml-0.5 align-text-bottom animate-[blink_1s_steps(2)_infinite] rounded-[1px]" />
                  </div>
                ) : (
                  <div className="px-4 py-2.5 rounded-2xl rounded-tl-sm bg-surface-secondary border border-subtle">
                    <span className="inline-block w-0.5 h-4 bg-brand animate-pulse align-middle" />
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
          </div>{/* end max-w-2xl */}
        </div>{/* end overflow-y-auto */}

        {/* ── Interjection addressed notice ─────────────────────── */}
        {interjectionAddressed && (
          <div className="border-t border-subtle bg-green-500/5 py-3">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
              <p className="text-xs text-green-400">Thanks — your input on this has already been captured. You can continue below if you have more to add.</p>
            </div>
          </div>
        )}

        {/* ── Input bar ────────────────────────────────────────────── */}
        {isClosed ? null : paused ? (
          <div className="border-t border-subtle bg-surface-page/95 backdrop-blur-sm py-4">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 flex items-center justify-center">
              <button
                onClick={async () => {
                  try {
                    await fetch(`${API_BASE}/api/discovery/portal/${token}/resume`, { method: 'POST' });
                  } catch { /* ignore, resume locally anyway */ }
                  setPaused(false);
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand/15 text-primary text-sm font-medium hover:bg-brand/25 transition-all"
              >
                Add More Info
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-subtle bg-surface-page/95 backdrop-blur-sm py-3">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || streaming}
                className="flex items-center justify-center w-10 h-10 rounded-xl text-content-muted hover:text-content-secondary hover:bg-surface-secondary transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                title="Attach file"
              >
                {uploading ? <Loader size={18} className="animate-spin" /> : <Paperclip size={18} />}
              </button>

              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Type your message…"
                disabled={streaming}
                rows={1}
                className="flex-1 resize-none bg-surface-secondary border border-default rounded-xl px-4 py-2.5 text-sm text-content placeholder:text-content-faint focus:outline-none focus:border-brand/40 focus:bg-surface-secondary transition-all duration-200 disabled:opacity-40 overflow-hidden max-h-[120px]"
              />

              {streaming ? (
                <button
                  type="button"
                  onClick={handleStop}
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-status-error/15 text-status-error hover:bg-status-error/25 transition-all duration-200 flex-shrink-0"
                  title="Stop generating"
                >
                  <StopCircle size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!input.trim()}
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-brand/20 text-primary hover:bg-brand/30 transition-all duration-200 disabled:opacity-25 disabled:cursor-not-allowed flex-shrink-0"
                  title="Send message"
                >
                  <Send size={18} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Blink keyframe (Tailwind JIT picks up arbitrary values in className but not @keyframes) */}
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
    </div>
  );
};

export default React.memo(ExternalPortal);
